/// <reference types="vite/client" />
// src/lib/notify.ts
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from "firebase/messaging";
import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import { track } from "./track";
import { addNotice } from "./notifications";

/* ----------------------------- Constants ----------------------------- */
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY;
const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ||
  (import.meta.env.DEV ? "http://localhost:3000/api" : "/api");
const LS_LAST_PUSH_TOKEN = "bw.push.token";
const RECENT_PUSH_TTL_MS = 2 * 60 * 1000;
const recentPushIds = new Map<string, number>();
let foregroundMessageUnsubscribe: (() => void) | null = null;

function rememberPushId(id?: string) {
  if (!id) return false;
  const now = Date.now();
  const last = recentPushIds.get(id);
  if (last && now - last < RECENT_PUSH_TTL_MS) return true;
  recentPushIds.set(id, now);
  for (const [key, ts] of recentPushIds.entries()) {
    if (now - ts > RECENT_PUSH_TTL_MS) recentPushIds.delete(key);
  }
  return false;
}

async function hashTokenShort(token: string): Promise<string> {
  try {
    if (typeof crypto === "undefined" || !crypto.subtle) return "nohash";
    const data = new TextEncoder().encode(token);
    const digest = await crypto.subtle.digest("SHA-256", data);
    const hex = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    return hex.slice(0, 6);
  } catch {
    return "nohash";
  }
}

async function ensureMessagingServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const ready = await navigator.serviceWorker.ready;
    return ready || null;
  } catch (err) {
    console.warn("[notify] sw register failed", err);
    return null;
  }
}

/* ----------------------------- Firebase App ----------------------------- */
function getFirebaseApp() {
  const existing = getApps();
  if (existing.length) return existing[0];
  const cfg = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string,
    appId: import.meta.env.VITE_FIREBASE_APP_ID as string,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID as string,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  };
  return initializeApp(cfg);
}

/* ----------------------------- Permission helpers ----------------------------- */
export async function ensureNotifPermissionOrRoute(nav?: (path: string) => void) {
  if (!("Notification" in window)) return;
  const status = (Notification as any).permission;
  if (status === "granted") return;
  if (status === "denied") {
    if (nav) nav("/onboarding/perm/notifications");
    return;
  }
  try {
    await (Notification as any).requestPermission?.();
  } catch {}
}

function canUsePush(): boolean {
  try {
    return "serviceWorker" in navigator && "PushManager" in window;
  } catch {
    return false;
  }
}

/* ----------------------------- Internal helper ----------------------------- */
async function postJSON(path: string, body: unknown) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const user = getAuth().currentUser;
  if (!user) throw new Error("not authed");
  const token = await user.getIdToken();
  headers.Authorization = `Bearer ${token}`;
  const base = API_BASE.replace(/\/$/, "");
  const finalPath = path.startsWith("/") ? path : `/${path}`;
  const res = await fetch(base + finalPath, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || (data as any)?.ok === false)
    throw new Error((data as any)?.error || res.statusText);
  return data;
}

type DispatchInput = {
  trigger: string;
  title: string;
  body: string;
  category?: string;
  deepLink?: string;
  data?: Record<string, string>;
};

export async function dispatchEngagementPush(input: DispatchInput) {
  const user = getAuth().currentUser;
  if (!user?.uid) throw new Error("not authed");
  return postJSON(`/notifications/dispatch`, {
    uid: user.uid,
    trigger: input.trigger,
    title: input.title,
    body: input.body,
    category: input.category,
    deepLink: input.deepLink,
    data: input.data,
  });
}

export async function sendTasksReminder() {
  return postJSON(`/notifications/tasks/remind`, {});
}

/* ----------------------------- FCM registration ----------------------------- */
let inflightGetToken: Promise<string | null> | null = null;
export async function initOrRefreshPushOnAuth(phoneHint?: string) {
  try {
    if (!(await isSupported())) {
      try {
        localStorage.setItem(LS_LAST_PUSH_TOKEN, `local-${Date.now()}`);
      } catch {}
      return;
    }
  } catch {
    return;
  }
  const user = getAuth().currentUser;
  if (!user) return;

  try {
    await ensureNotifPermissionOrRoute();
    if (!("Notification" in window) || Notification.permission !== "granted") return;
  } catch {}

  const app = getFirebaseApp();
  const messaging = getMessaging(app);
  if (!VAPID_KEY) {
    console.warn("[notify] Missing VITE_FCM_VAPID_KEY; push will not register.");
    return;
  }

  const swReg = await ensureMessagingServiceWorker();
  if (!swReg) return;

  const doGet = async () => {
    return await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
  };

  try {
    const retryGet = async () => {
      inflightGetToken = inflightGetToken || doGet();
      let fcmToken = await inflightGetToken;
      inflightGetToken = null;
      if (!fcmToken) {
        await new Promise((r) => setTimeout(r, 800));
        fcmToken = await doGet();
      }
      return fcmToken;
    };

    let fcmToken = await retryGet();
    if (!fcmToken) {
      await new Promise((r) => setTimeout(r, 1200));
      fcmToken = await retryGet();
    }
    if (!fcmToken) return;

    const registrationKey = `${user.uid}:${fcmToken}`;
    const last = localStorage.getItem(LS_LAST_PUSH_TOKEN) || "";
    if (last !== registrationKey) {
      try {
        await postJSON(`/push/register`, {
          token: fcmToken,
          platform: "web",
          phone_hint: phoneHint || null,
        });
        localStorage.setItem(LS_LAST_PUSH_TOKEN, registrationKey);
        console.log("✅ Push token registered");
        track("push_registered", { phone: phoneHint || null });
        if (import.meta.env.DEV) {
          const tokenHash = await hashTokenShort(fcmToken);
          const deviceId = localStorage.getItem("bw.device.id") || "not_set";
          let deviceCount = "unknown";
          try {
            const user = getAuth().currentUser;
            const token = await user?.getIdToken();
            if (token) {
              const res = await fetch(`${API_BASE.replace(/\/$/, "")}/push/status`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await res.json().catch(() => ({}));
              if (data?.ok) deviceCount = String(data.count ?? "0");
            }
          } catch {}
          console.debug("[push] registered", { tokenHash, deviceId, deviceCount });
        }
      } catch (e) {
        console.warn("⚠️ Failed to register push token", e);
        track("push_registration_failed", { reason: (e as Error)?.message });
        throw e;
      }
    }

    try {
      if (!foregroundMessageUnsubscribe) {
        foregroundMessageUnsubscribe = onMessage(messaging, (payload) => {
        console.log("[notify] foreground message", payload);
        // Surface a visible notification while in foreground so Send Test has feedback
        const title = (payload as any)?.notification?.title || "BiteWise";
        const body = (payload as any)?.notification?.body || "You have a new update!";
        const data = (payload as any)?.data || {};
        const noticeId =
          (typeof data.notice_id === "string" && data.notice_id) ||
          (payload as any)?.messageId ||
          "";
        if (noticeId && rememberPushId(noticeId)) {
          return;
        }
        const kind = String(data.notice_kind || "").trim();
        const noticeTitle = String(data.notice_title || "").trim();
        if (kind && noticeTitle) {
          const allowed = new Set(["price", "tasks", "savings", "milestone", "system"]);
          const sourceRaw = String(data.notice_source || "").trim();
          const source = sourceRaw === "details" || sourceRaw === "system" ? sourceRaw : undefined;
          if (allowed.has(kind)) {
            const ts = Number(data.notice_ts) || Date.now();
            addNotice({
              id: typeof data.notice_id === "string" ? data.notice_id : undefined,
              kind: kind as any,
              title: noticeTitle,
              body: typeof data.notice_body === "string" ? data.notice_body : undefined,
              ts,
              source: source as any,
              deepLink:
                typeof data.notice_deepLink === "string" && data.notice_deepLink
                  ? data.notice_deepLink
                  : typeof data.deep_link === "string" && data.deep_link
                  ? data.deep_link
                  : undefined,
            });
          }
        }
        if (typeof document !== "undefined" && document.visibilityState === "visible") {
          return;
        }
        void sendLocalNotification(title, body, { tag: noticeId || undefined, allowWhenVisible: false });
        });
      }
    } catch {}
  } catch (err) {
    console.warn("[notify] getToken failed", err);
    track("push_registration_failed", { reason: (err as Error)?.message });
  }
}

export async function disablePush(): Promise<void> {
  if (foregroundMessageUnsubscribe) {
    try {
      foregroundMessageUnsubscribe();
    } catch {
      /* ignore */
    }
    foregroundMessageUnsubscribe = null;
  }
  try {
    if (!(await isSupported())) return;
  } catch {
    return;
  }
  try {
    const app = getFirebaseApp();
    const messaging = getMessaging(app);
    await deleteToken(messaging).catch(() => {});
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(LS_LAST_PUSH_TOKEN);
  } catch {}
}

/* ----------------------------- Local test notification ----------------------------- */
export async function sendLocalTestNotification(
  title = "BiteWise test notification",
  body = "If you see this, notifications are working on this device."
): Promise<boolean> {
  try {
    if (!("Notification" in window)) return false;
    if (Notification.permission !== "granted") return false;
    if (canUsePush()) {
      try {
        const reg = await (navigator as any).serviceWorker.ready;
        await reg.showNotification(title, { body, icon: "/icons/icon-512.png" });
        return true;
      } catch {}
    }
    new Notification(title, { body, icon: "/icons/icon-512.png" });
    return true;
  } catch {
    return false;
  }
}

export async function sendLocalNotification(
  title: string,
  body: string,
  opts?: { tag?: string; allowWhenVisible?: boolean }
): Promise<boolean> {
  try {
    if (!("Notification" in window)) return false;
    if (Notification.permission !== "granted") return false;
    if (opts?.allowWhenVisible === false && document.visibilityState === "visible") return false;
    if (canUsePush()) {
      try {
        const reg = await (navigator as any).serviceWorker.ready;
        await reg.showNotification(title, {
          body,
          icon: "/icons/icon-512.png",
          tag: opts?.tag,
          renotify: false,
        });
        return true;
      } catch {}
    }
    new Notification(title, {
      body,
      icon: "/icons/icon-512.png",
      tag: opts?.tag,
      renotify: false,
    } as any);
    return true;
  } catch {
    return false;
  }
}
